import { formatDate } from '@angular/common';
import {
  Directive,
  ElementRef,
  forwardRef,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { DefaultValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DatetimeService } from '~app/services/datetime.service';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

@Directive({
  selector: '[ciaoDateTimeCustomAccessor]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimeCustomAccessorDirective),
      multi: true,
    },
  ],
})
export class DateTimeCustomAccessorDirective
  extends DefaultValueAccessor
  implements OnInit, OnDestroy
{
  private lastModelValue: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    elementRef: ElementRef,
    renderer: Renderer2,
    private readonly dateTimeService: DatetimeService
  ) {
    super(renderer, elementRef, false);
  }
  override writeValue(modelValue: string): void {
    this.lastModelValue = modelValue ?? null;
    const viewValue = this.modelToView(modelValue);
    super.writeValue(viewValue);
  }
  override registerOnChange(fn: (viewValue: string) => void): void {
    const fn2 = (viewValue: string) => {
      const modelValue = this.viewToModel(viewValue);
      fn(modelValue);
      return {};
    };
    super.registerOnChange(fn2);
  }

  ngOnInit(): void {
    this.dateTimeService.timeZone$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.lastModelValue != null) {
          const viewValue = this.modelToView(this.lastModelValue);
          super.writeValue(viewValue);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private modelToView(isoStringValue: string): string | null {
    if (!isoStringValue) {
      return null;
    }
    const normalized =
      this.dateTimeService.normalizeToUtcIfNoOffset(isoStringValue);
    const date = new Date(normalized);
    if (Number.isNaN(date.valueOf())) {
      return '';
    }
    const tz =
      this.dateTimeService.timeZone$.value ||
      this.dateTimeService.browserDefaults.timeZone;
    return formatDate(date, 'yyyy-MM-ddTHH:mm', 'en-US', tz);
  }

  private viewToModel(formattedDate: string): string {
    if (!formattedDate) {
      return '';
    }
    const tz =
      this.dateTimeService.timeZone$.value ||
      this.dateTimeService.browserDefaults.timeZone;
    const parsed = dayjs.tz(formattedDate, 'YYYY-MM-DDTHH:mm', tz);
    return parsed.isValid() ? parsed.toISOString() : '';
  }
}
